import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery, type QueryKey } from "@tanstack/react-query";

import {
  DEFAULT_PAGE_SIZE,
  listPath,
  type Facets,
  type ListQuery,
  type PagedResponse,
  type PageInfo,
} from "@/lib/pagedList";
import { useRestClient } from "@/lib/restClient";
import { useListPageSize } from "@/hooks/useListPageSize";

/** What a paged list screen renders, whether the server or the client cuts the pages. */
export interface ListPages<Row> {
  /** The rows of the current page. */
  rows: Row[];
  /** 1-based current page; `pageCount` is at least 1. */
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  /** Index of the first row of the current page among the matching rows. */
  offset: number;
  /** Rows matching the search and filters, and rows before them. */
  totalCount: number;
  unfilteredCount: number;
}

export interface ServerListPages<Row> extends ListPages<Row> {
  facets: Facets;
  isPending: boolean;
  isError: boolean;
  /** A page is loading (first load, next page or a new search). */
  isFetching: boolean;
  /** The current rows answer a previous search while the new one loads. */
  isPlaceholderData: boolean;
  refetch: () => Promise<unknown>;
}

interface UsePagedListOptions {
  /** Cache key prefix; mutations invalidate it. The list query is appended. */
  queryKey: QueryKey;
  /** Collection path, e.g. `/api/tenants`. */
  path: string;
  /** Envelope key of the rows, e.g. `tenants`. */
  itemsKey: string;
  query?: ListQuery;
  pageSize?: number;
  enabled?: boolean;
  /** Polling interval while the predicate holds (e.g. transfers still running). */
  refetchInterval?: (rows: readonly unknown[]) => number | false;
}

/**
 * One server-paged collection: search, filters and sort go to the API, which
 * answers one page and the facet counts. Pages already visited stay cached, so
 * Previous is instant; Next loads the following cursor. A new search or filter
 * restarts at page 1.
 */
export function usePagedList<Row>({
  queryKey,
  path,
  itemsKey,
  query = {},
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
  refetchInterval,
}: UsePagedListOptions): ServerListPages<Row> {
  const apiFetch = useRestClient();
  const [pageSize, setPageSize] = useListPageSize(initialPageSize);
  const request = listPath(path, query);
  const positionKey = JSON.stringify([request, pageSize]);
  const [position, setPosition] = useState({ key: positionKey, page: 1 });
  const page = position.key === positionKey ? position.page : 1;
  if (position.key !== positionKey) setPosition({ key: positionKey, page: 1 });

  const result = useInfiniteQuery({
    queryKey: [...queryKey, request, pageSize],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      apiFetch<PagedResponse<string, Row>>(
        listPath(path, query, { first: pageSize, after: pageParam }),
        { signal },
      ),
    getNextPageParam: (last) =>
      last.page_info?.has_next_page ? (last.page_info.end_cursor ?? undefined) : undefined,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[previousQuery.queryKey.length - 1] === pageSize
        ? keepPreviousData(previous)
        : undefined,
    refetchInterval: refetchInterval
      ? (current) =>
          refetchInterval(current.state.data?.pages.flatMap((entry) => entry[itemsKey] ?? []) ?? [])
      : undefined,
  });

  const pages = result.data?.pages ?? [];
  const info: PageInfo | undefined = pages[0]?.page_info;
  const totalCount = info?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const current = Math.min(page, Math.max(1, pages.length));
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = result;

  // Moving forward loads the pages between the last one fetched and the target.
  useEffect(() => {
    if (page > pages.length && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [page, pages.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rows = useMemo(
    () => (pages[current - 1]?.[itemsKey] ?? []) as Row[],
    [pages, current, itemsKey],
  );

  return {
    rows,
    page: current,
    pageCount,
    setPage: (next) =>
      setPosition({ key: positionKey, page: Math.max(1, Math.min(next, pageCount)) }),
    pageSize,
    setPageSize,
    offset: (current - 1) * pageSize,
    totalCount,
    unfilteredCount: info?.unfiltered_count ?? 0,
    facets: pages[0]?.facets ?? {},
    isPending: result.isPending,
    isError: result.isError,
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
    refetch: result.refetch,
  };
}

/**
 * The same paging over rows already loaded, for screens whose filters depend
 * on code the interface ships (widget availability, report kinds, derived
 * access summaries). `resetKey` returns to page 1 when the filters change.
 */
export function useClientPages<Row>(
  rows: readonly Row[],
  resetKey: string,
  initialPageSize = DEFAULT_PAGE_SIZE,
  unfilteredCount = rows.length,
): ListPages<Row> {
  const [pageSize, setPageSize] = useListPageSize(initialPageSize);
  const positionKey = JSON.stringify([resetKey, pageSize]);
  const [position, setPosition] = useState({ key: positionKey, page: 1 });
  const page = position.key === positionKey ? position.page : 1;
  if (position.key !== positionKey) setPosition({ key: positionKey, page: 1 });
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const offset = (current - 1) * pageSize;
  const visible = useMemo(() => rows.slice(offset, offset + pageSize), [rows, offset, pageSize]);
  return {
    rows: visible,
    page: current,
    pageCount,
    setPage: (next) =>
      setPosition({ key: positionKey, page: Math.max(1, Math.min(next, pageCount)) }),
    pageSize,
    setPageSize,
    offset,
    totalCount: rows.length,
    unfilteredCount,
  };
}
